import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'
import { useFetchedJobsStore } from 'stores/fetchedJobs.js'

const PAGE_SIZE = 25

// `status` is which companies are listed: all of them, the ones whose LinkedIn page was read, the ones still
// waiting for it, or the ones the user blocked. `website` limits the list to companies whose own website was read too.
const defaultFilters = () => ({ q: '', status: 'all', website: '', sort: 'recent' })

function buildQuery(filters, page) {
  const params = new URLSearchParams()
  params.set('page[number]', page)
  params.set('page[limit]', PAGE_SIZE)
  if (filters.q.trim()) params.set('q', filters.q.trim())
  if (filters.status !== 'all') params.set('status', filters.status)
  if (filters.website) params.set('website', filters.website)
  params.set('sort', filters.sort)
  return params.toString()
}

// The companies behind the saved jobs: the searchable list on the left of the Companies page, and the company
// open on the right. Mirrors the jobs store (stores/fetchedJobs.js).
export const useJobCompaniesStore = defineStore('jobCompanies', {
  state: () => ({
    companies: [],
    pageInfo: { current_page: 0, total_pages: 0, total_records: 0 },
    counts: { all: 0, fetched: 0, pending: 0, website_read: 0, blocked: 0 }, // across everything the user has, ignoring the filters
    filters: defaultFilters(),
    loaded: false,
    loading: false,
    loadingMore: false,
    loadMoreError: false,
    refreshing: false,
    error: null,

    selectedId: null,
    details: {}, // full company by id
    detailLoading: false,
    detailError: null,

    requestId: 0, // only the newest request is used
  }),

  getters: {
    hasMore: (state) => state.pageInfo.current_page < state.pageInfo.total_pages,
    isFiltered: (state) => Boolean(state.filters.q.trim() || state.filters.status !== 'all' || state.filters.website),
    selected: (state) => (state.selectedId ? state.details[state.selectedId] || null : null),
  },

  actions: {
    // What the search box suggests for what has been typed (company names, industries, headquarters), from all the user's companies, not just
    // the loaded page. Gives [] when it cannot be loaded: a missing suggestion is not worth an error message.
    async suggest(query) {
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', `/api/v1/job_companies/suggestions?${new URLSearchParams({ q: query.trim() })}`, {}, false)
        return !res.js_error && res.response.success ? res.response.data.suggestions : []
      } catch {
        return []
      }
    },

    async fetchCompanies() {
      const requestId = ++this.requestId
      this.loading = true
      this.error = null
      this.loadMoreError = false

      const { axiosRequest } = useRequest()
      let res
      try {
        res = await axiosRequest('GET', `/api/v1/job_companies?${buildQuery(this.filters, 1)}`, {}, false)
      } catch {
        res = { js_error: true }
      }
      if (requestId !== this.requestId) return

      if (!res.js_error && res.response?.success) {
        this.companies = res.response.data.records
        this.pageInfo = res.response.data.page
        this.counts = res.response.data.counts
        this.loaded = true
      } else {
        this.error = res.response?.message || 'Failed to load your companies.'
      }
      this.loading = false
    },

    // Reloads what is on screen without changing what the user is looking at: same filters, same open company.
    async refresh() {
      this.refreshing = true
      try {
        const openId = this.selectedId
        await this.fetchCompanies()

        const kept = {}
        if (openId) {
          const { axiosRequest } = useRequest()
          let res
          try {
            res = await axiosRequest('GET', `/api/v1/job_companies/${openId}`, {}, false)
          } catch {
            res = { js_error: true }
          }
          if (!res.js_error && res.response?.success) kept[openId] = res.response.data
          else if (this.details[openId]) kept[openId] = this.details[openId]
        }
        this.details = kept
      } finally {
        this.refreshing = false
      }
    },

    async loadMore() {
      if (!this.hasMore || this.loadingMore || this.loading) return

      const requestId = this.requestId
      this.loadingMore = true
      this.loadMoreError = false
      const { axiosRequest } = useRequest()
      let res
      try {
        res = await axiosRequest('GET', `/api/v1/job_companies?${buildQuery(this.filters, this.pageInfo.current_page + 1)}`, {}, false)
      } catch {
        res = { js_error: true }
      }
      this.loadingMore = false
      if (requestId !== this.requestId) return

      if (!res.js_error && res.response?.success) {
        const known = new Set(this.companies.map((company) => company.id))
        this.companies.push(...res.response.data.records.filter((company) => !known.has(company.id)))
        this.pageInfo = res.response.data.page
      } else {
        this.loadMoreError = true
      }
    },

    setFilters(patch) {
      this.filters = { ...this.filters, ...patch }
      return this.fetchCompanies()
    },

    clearFilters() {
      this.filters = defaultFilters()
      return this.fetchCompanies()
    },

    // Marks a company as a recruiter ('recruiter') or as an individual company ('company'). Returns { ok, message }. The jobs store is told, so
    // the jobs of this company follow.
    async setKind(id, kind) {
      const { axiosRequest } = useRequest()
      let res
      try {
        res = await axiosRequest('PATCH', `/api/v1/job_companies/${id}`, { kind }, false)
      } catch {
        res = { js_error: true }
      }
      if (res.js_error || !res.response?.success) {
        return { ok: false, message: res.response?.message || 'Could not save that. Please try again.' }
      }

      const saved = res.response.data.kind
      if (this.details[id]) this.details[id].kind = saved
      const listed = this.companies.find((company) => company.id === id)
      if (listed) listed.kind = saved
      useFetchedJobsStore().companyKindChanged(id, saved)
      return { ok: true }
    },

    // Blocks a company (its jobs leave Job Listings) or unblocks it. Returns { ok, message }. The jobs store is told, so its list and totals follow.
    async setBlocked(id, blocked) {
      const { axiosRequest } = useRequest()
      let res
      try {
        res = await axiosRequest('PATCH', `/api/v1/job_companies/${id}`, { blocked }, false)
      } catch {
        res = { js_error: true }
      }
      if (res.js_error || !res.response?.success) {
        return { ok: false, message: res.response?.message || 'Could not save that. Please try again.' }
      }

      const blockedAt = res.response.data.blocked_at || null
      if (this.details[id]) this.details[id].blocked_at = blockedAt
      const listed = this.companies.find((company) => company.id === id)
      if (listed) listed.blocked_at = blockedAt
      this.counts.blocked += blockedAt ? 1 : -1
      useFetchedJobsStore().companyBlockChanged(id, blockedAt)
      return { ok: true }
    },

    async select(id) {
      this.selectedId = id
      this.detailError = null
      if (!id || this.details[id]) return

      this.detailLoading = true
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('GET', `/api/v1/job_companies/${id}`, {}, false)
      if (this.selectedId !== id) return

      if (!res.js_error && res.response?.success) this.details[id] = res.response.data
      else this.detailError = res.response?.message || 'Failed to load this company.'
      this.detailLoading = false
    },
  },
})
